export type GoalFormat = 'number' | 'percent' | 'dollars';

// The secondaryKpi to seed kpiGoal with before reconciling against the KPI rows.
// Conversion is intentionally stripped from the KPI rows, so it must be carried
// forward from defaults (preserving its value) or it would be lost. CTR/VCR are
// deletable rows driven by the rows themselves — seeding them here would resurrect
// a row the user just deleted, so anything other than Conversion falls back to
// the Conversion sentinel.
export function resolveSeedSecondaryKpi(defaultKpiGoal: any): Array<Record<string, string>> {
  const defaultSecondaryKpi = defaultKpiGoal?.secondaryKpi;
  const defaultSecondaryKpiName =
    Array.isArray(defaultSecondaryKpi) && defaultSecondaryKpi.length > 0
      ? Object.keys(defaultSecondaryKpi[0] || {})[0]
      : undefined;

  return defaultSecondaryKpiName === 'Conversion' ? [...defaultSecondaryKpi] : [{ Conversion: '0' }];
}

export interface GlobalKpi {
  name: string;
  format: GoalFormat;
  value: number | null;
}

export interface KpiOptions {
  providers: string[];
  global_kpis: GlobalKpi[];

  // derived helpers
  kpis: string[]; // list of KPI names
  goal_formats: GoalFormat[];
  kpi_goal_formats: Record<string, GoalFormat>;
  kpi_default_values: Record<string, number>;
}

export const EMPTY_KPI_OPTIONS: KpiOptions = {
  providers: [],
  global_kpis: [],
  kpis: [],
  goal_formats: ['number', 'percent', 'dollars'],
  kpi_goal_formats: {},
  kpi_default_values: {},
};

const toStrList = (arr: any): string[] =>
  Array.isArray(arr)
    ? arr
        .map((v) => (typeof v === 'string' ? v : v?.label ?? v?.name ?? ''))
        .filter(Boolean)
    : [];

export function normalizeKpiOptionsResponse(resp: any): KpiOptions {
  const looks404 = resp?.code === 404 || resp?.exception === 'RecordNotFound';

  if (looks404 || !resp) {
    return EMPTY_KPI_OPTIONS;
  }

  // normalize providers
  const providers = toStrList(resp?.providers);

  // normalize global_kpis
  const rawGlobal = Array.isArray(resp?.global_kpis) ? resp.global_kpis : [];

  const kpiGoalFormats: Record<string, GoalFormat> = {};
  const kpiDefaultValues: Record<string, number> = {};
  const kpis: string[] = [];

  rawGlobal.forEach((k: any) => {
    const name = typeof k?.name === 'string' ? k.name : null;
    if (!name) return;

    kpis.push(name);

    if (k?.format) {
      kpiGoalFormats[name] = k.format as GoalFormat;
    }

    // allow 0, but ignore null/undefined
    if (k?.value !== null && k?.value !== undefined) {
      kpiDefaultValues[name] = k.value;
    }
  });

  // derive goal_formats from map, with fallback
  let goalFormats = Array.from(new Set(Object.values(kpiGoalFormats))) as GoalFormat[];
  if (goalFormats.length === 0) {
    goalFormats = ['number', 'percent', 'dollars'];
  }

  return {
    providers,
    global_kpis: rawGlobal.map((k: any) => ({
      name: k?.name,
      format: k?.format as GoalFormat,
      value: k?.value ?? null,
    })),
    kpis,
    goal_formats: goalFormats,
    kpi_goal_formats: kpiGoalFormats,
    kpi_default_values: kpiDefaultValues,
  }
}
